import { Dispatch, SetStateAction, useCallback } from 'react';

import { useRouter } from 'expo-router';

import { IBackendCartEntries, ILegacyCartEntry } from '@rbi-ctg/menu';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import { IUseCart } from 'hooks/use-cart-types';
import { useHasAvailableFeatureMenuAddOn } from 'hooks/use-feature-menu-add-on';
import { getLivePathname } from 'navigation/live-pathname-tracker';
import {
  redirectFromOrderDetailsModal,
  shouldLeaveIosOrderDetailsModal,
} from 'navigation/redirect-from-order-details-modal';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { TRANSITION_ANIMATION_TIME_MS } from 'utils/constants';
import { isIos, isWeb } from 'utils/environment';
import { routes } from 'utils/routing';

import { IPendingReorder } from '../types';

type UseHandleReorderInput = {
  addToCart: IUseCart['addItemToCart'];
  setPendingReorder: Dispatch<SetStateAction<IPendingReorder | null>>;
  setReordering: (reordering: boolean) => void;
  setUnavailableCartEntries: (unavailableCartEntries: ILegacyCartEntry[]) => void;
  setReorderedOrderId: Dispatch<SetStateAction<string | null>>;
};

/**
 *
 * Returns a function to reorder a previous order.
 */
export const useHandleReorder = ({
  addToCart,
  setPendingReorder,
  setReordering,
  setUnavailableCartEntries,
  setReorderedOrderId,
}: UseHandleReorderInput) => {
  const mapBackendToFrontend = useMapToFrontendCartEntry();
  const enableAddExtras = useFlag(LaunchDarklyFlag.ENABLE_ADD_EXTRAS);
  const { hasAvailableAddOns } = useHasAvailableFeatureMenuAddOn();

  const router = useRouter();
  const finishReorder = useCallback(() => {
    setReordering(false);
    setReorderedOrderId(null);
  }, [setReordering, setReorderedOrderId]);

  // This will force to wait for hydration to finish before going to the cart,
  // Otherwise the cart will check for empty entries and will redirect back to the menu.
  // The path is read when the timer fires, not when reorder starts: during the delay the
  // user can still dismiss the iOS sheet, and a stale path would pop one screen too many.
  const redirect = useCallback(
    (url: string, onNavigated?: () => void) => {
      setTimeout(() => {
        redirectFromOrderDetailsModal(router, url, isIos, getLivePathname, () => {
          // Keep the reorder loader visible until navigation has finished.
          finishReorder();
          onNavigated?.();
        });
      }, 2000);
    },
    [router, finishReorder]
  );

  const shouldDisplayWebToast = isWeb;

  const processEntry = useCallback(
    async (entry: IBackendCartEntries, reorderCartEntries: IBackendCartEntries[]) => {
      const { cartEntry: frontendEntry, status } = await mapBackendToFrontend(
        entry,
        (reorderCartEntries as unknown) as ILegacyCartEntry[]
      );

      return { frontendEntry, status };
    },
    [mapBackendToFrontend]
  );

  return useCallback(
    async (order: IPendingReorder, url = routes.orders) => {
      if (!order.cart.reorderCartEntries || !('rbiOrderId' in order)) {
        // if there's no order, navigate to where was initially intended
        redirect(url);
        return;
      }

      setReordering(true);
      setReorderedOrderId(order.rbiOrderId);

      const { reorderCartEntries } = order.cart;

      const availableEntries: ILegacyCartEntry[] = [];
      const unavailableEntries: ILegacyCartEntry[] = [];

      const processedEntries = await Promise.all(
        reorderCartEntries.map(entry => processEntry(entry, reorderCartEntries))
      );

      for (const entry of processedEntries) {
        const { frontendEntry, status } = entry;

        if (status === ItemAvailabilityStatus.AVAILABLE) {
          availableEntries.push(frontendEntry);
        } else {
          unavailableEntries.push(frontendEntry);
        }
      }

      availableEntries.forEach((entry: ILegacyCartEntry) =>
        addToCart(entry, undefined, !shouldDisplayWebToast)
      );

      if (availableEntries.length && shouldDisplayWebToast) {
        const [firstEntry] = availableEntries;

        const message =
          availableEntries.length > 1
            ? t('addToCartMultipleSuccess', {
                itemName: firstEntry.name,
                quantity: availableEntries.length - 1,
              })
            : t('addToCartSuccess', { itemName: firstEntry.name });

        toaster.addToCartToast(message, firstEntry.image);
      }

      setPendingReorder(null);

      const allEntriesAreUnavailable = unavailableEntries.length === reorderCartEntries.length;

      // If some of the entries are available, we must add them to the cart for reordering.
      if (allEntriesAreUnavailable) {
        // iOS can't present the unavailable-items modal while the order details sheet
        // is still on screen, so there it is shown only once the sheet is gone.
        if (shouldLeaveIosOrderDetailsModal(router, getLivePathname(), isIos)) {
          redirect(url, () => setUnavailableCartEntries(unavailableEntries));
        } else {
          setUnavailableCartEntries(unavailableEntries);
          if (getLivePathname() === url) {
            // Already on the destination: a delayed redirect would only race with the
            // modal's own confirm navigation (e.g. to the cart) and bounce the user back.
            finishReorder();
          } else {
            redirect(url);
          }
        }
        return;
      }

      const containsExtras = processedEntries.some(
        entry => entry.status === ItemAvailabilityStatus.CART_EXTRA
      );

      if (enableAddExtras && containsExtras && hasAvailableAddOns()) {
        await new Promise(resolve => setTimeout(resolve, TRANSITION_ANIMATION_TIME_MS)); // just in case
        // If the order had extras go to the extras modal in the cart (but after we get there from previous step)
        redirect(routes.cartAddExtras);
        return;
      }

      redirect(routes.cart);
      return;
    },
    [
      setReordering,
      setReorderedOrderId,
      shouldDisplayWebToast,
      setPendingReorder,
      enableAddExtras,
      hasAvailableAddOns,
      redirect,
      processEntry,
      addToCart,
      setUnavailableCartEntries,
      router,
      finishReorder,
    ]
  );
};
